import React, { useEffect, useState } from "react";
import { Box, Button, DatePicker, Flex, Heading, Notification, Table, Text, TextField } from "@telia/teddy";
import { API_URL_SUPERADMIN_COMMISSION, API_URL_SUPERADMIN_COMMISSIONS } from "../../api";

type CommissionRate = {
    amount: string;
    chain: string | null;
    createdAt: string;
    createdBy: string;
    endDate: string | null;
    id: string;
    salesType: string;
    startDate: string;
};

type FormState = {
    amount: string;
    chain: string;
    endDate: string;
    salesType: string;
    startDate: string;
};

const emptyForm: FormState = {
    salesType: "",
    amount: "",
    chain: "",
    startDate: "",
    endDate: "",
};

const CHAIN_LABELS: Record<string, string> = {
    ELKJOP: "Elkjøp",
    POWER: "Power",
    D2D: "D2D",
    TM_INTERNAL: "TM intern",
    TM_EXTERNAL: "TM ekstern",
};

const formatDate = (iso: string | null): string => {
    if (!iso) return "–";
    return new Date(iso).toLocaleDateString("nb-NO");
};

const CommissionAdmin: React.FC = () => {
    const [commissions, setCommissions] = useState<CommissionRate[]>([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [sortKey, setSortKey] = useState<keyof CommissionRate>("salesType");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
    const [showExpired, setShowExpired] = useState(false);

    const handleSort = (key: keyof CommissionRate) => {
        if (key === sortKey) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const now = new Date();
    const visibleCommissions = showExpired
        ? commissions
        : commissions.filter((c) => !c.endDate || new Date(c.endDate) >= now);

    const sortedCommissions = [...visibleCommissions].sort((a, b) => {
        const aVal = a[sortKey] ?? "";
        const bVal = b[sortKey] ?? "";
        const cmp = String(aVal).localeCompare(String(bVal), "nb", { numeric: true });
        return sortDir === "asc" ? cmp : -cmp;
    });

    const sortIndicator = (key: keyof CommissionRate) => {
        if (key !== sortKey) return " ↕";
        return sortDir === "asc" ? " ↑" : " ↓";
    };

    const fetchCommissions = () => {
        setLoading(true);
        fetch(API_URL_SUPERADMIN_COMMISSIONS)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`Serverfeil: ${res.status} ${res.statusText}`);
                }
                return res.json() as Promise<CommissionRate[]>;
            })
            .then((data) => {
                if (!Array.isArray(data)) {
                    throw new Error("Uventet svar fra server");
                }
                setCommissions(data);
                setLoading(false);
            })
            .catch((err: Error) => {
                setError(err.message || "Kunne ikke laste provisjoner");
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchCommissions();
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const payload = {
            salesType: form.salesType.trim(),
            amount: parseFloat(form.amount),
            chain: form.chain || null,
            startDate: form.startDate,
            endDate: form.endDate || null,
        };

        const isEdit = editingId !== null;
        const url = isEdit ? API_URL_SUPERADMIN_COMMISSION(editingId) : API_URL_SUPERADMIN_COMMISSIONS;

        fetch(url, {
            method: isEdit ? "PUT" : "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        })
            .then(async (res) => {
                if (!res.ok) {
                    const json = await res.json().catch(() => ({}));
                    throw new Error((json as { error?: string }).error ?? "Noe gikk galt");
                }
                setForm(emptyForm);
                setEditingId(null);
                fetchCommissions();
            })
            .catch((err: Error) => setError(err.message));
    };

    const handleEdit = (commission: CommissionRate) => {
        setEditingId(commission.id);
        setForm({
            salesType: commission.salesType,
            amount: commission.amount,
            chain: commission.chain ?? "",
            startDate: commission.startDate.slice(0, 10),
            endDate: commission.endDate ? commission.endDate.slice(0, 10) : "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = (id: string) => {
        if (!window.confirm("Er du sikker på at du vil slette denne provisjonen?")) return;
        fetch(API_URL_SUPERADMIN_COMMISSION(id), { method: "DELETE" })
            .then((res) => {
                if (!res.ok) throw new Error();
                fetchCommissions();
            })
            .catch(() => setError("Kunne ikke slette provisjon"));
    };

    const handleCancel = () => {
        setEditingId(null);
        setForm(emptyForm);
        setError(null);
    };

    return (
        <div className="retailer-dashboard-wrapper">
            <Heading as="h1" variant="title-300" mb="400">
                Provisjonssatser
            </Heading>

            <Box mb="600">
                <Heading as="h2" variant="title-200" mb="300">
                    {editingId ? "Rediger provisjon" : "Legg til ny provisjon"}
                </Heading>

                {error && (
                    <Notification type="tile" variant="error" mb="300">
                        <Notification.Icon />
                        <Notification.Text>{error}</Notification.Text>
                    </Notification>
                )}

                <form onSubmit={handleSubmit}>
                    <Flex direction="column" gap="300" style={{ maxWidth: "480px" }}>
                        <Flex gap="300">
                            <Box style={{ flex: 1 }}>
                                <TextField>
                                    <TextField.Label>Sales type</TextField.Label>
                                    <TextField.Input
                                        value={form.salesType}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setForm((f) => ({ ...f, salesType: e.target.value }))
                                        }
                                    />
                                </TextField>
                            </Box>

                            <Box style={{ flex: 1 }}>
                                <label
                                    htmlFor="chain-select"
                                    style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}
                                >
                                    Kjede
                                </label>
                                <select
                                    id="chain-select"
                                    value={form.chain}
                                    onChange={(e) => setForm((f) => ({ ...f, chain: e.target.value }))}
                                    style={{ width: "100%", padding: "8px", fontSize: "inherit" }}
                                >
                                    <option value="">Alle</option>
                                    <option value="ELKJOP">Elkjøp</option>
                                    <option value="POWER">Power</option>
                                    <option value="D2D">D2D</option>
                                    <option value="TM_INTERNAL">TM intern</option>
                                    <option value="TM_EXTERNAL">TM ekstern</option>
                                </select>
                            </Box>
                        </Flex>

                        <TextField>
                            <TextField.Label>Beløp</TextField.Label>
                            <TextField.Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.amount}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setForm((f) => ({ ...f, amount: e.target.value }))
                                }
                            />
                        </TextField>

                        <Flex gap="300">
                            <Box style={{ flex: 1 }}>
                                <DatePicker
                                    label="Startdato"
                                    value={form.startDate}
                                    onSelectedDate={(date) =>
                                        setForm((f) => ({
                                            ...f,
                                            startDate: date ? date.toISOString().slice(0, 10) : "",
                                        }))
                                    }
                                />
                            </Box>

                            <Box style={{ flex: 1 }}>
                                <DatePicker
                                    label="Sluttdato (valgfri)"
                                    value={form.endDate}
                                    min={form.startDate}
                                    onSelectedDate={(date) =>
                                        setForm((f) => ({
                                            ...f,
                                            endDate: date ? date.toISOString().slice(0, 10) : "",
                                        }))
                                    }
                                />
                            </Box>
                        </Flex>

                        <Flex gap="200">
                            <Button type="submit" variant="primary">
                                {editingId ? "Lagre endringer" : "Legg til"}
                            </Button>
                            {editingId && (
                                <Button type="button" variant="secondary" onClick={handleCancel}>
                                    Avbryt
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                </form>
            </Box>

            <Heading as="h2" variant="title-200" mb="300">
                Alle provisjoner
            </Heading>

            <Flex align="center" gap="200" mb="300">
                <input
                    id="show-expired"
                    type="checkbox"
                    checked={showExpired}
                    onChange={(e) => setShowExpired(e.target.checked)}
                />
                <label htmlFor="show-expired" style={{ cursor: "pointer" }}>
                    Vis utgåtte
                </label>
            </Flex>

            {loading ? (
                <Text>Laster...</Text>
            ) : (
                <Table style={{ width: "100%", tableLayout: "auto" }}>
                    <Table.Header>
                        <Table.Row>
                            <Table.Head
                                onClick={() => handleSort("salesType")}
                                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            >
                                Sales type{sortIndicator("salesType")}
                            </Table.Head>
                            <Table.Head
                                onClick={() => handleSort("chain")}
                                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            >
                                Kjede{sortIndicator("chain")}
                            </Table.Head>
                            <Table.Head
                                onClick={() => handleSort("amount")}
                                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            >
                                Beløp{sortIndicator("amount")}
                            </Table.Head>
                            <Table.Head
                                onClick={() => handleSort("startDate")}
                                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            >
                                Startdato{sortIndicator("startDate")}
                            </Table.Head>
                            <Table.Head
                                onClick={() => handleSort("endDate")}
                                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            >
                                Sluttdato{sortIndicator("endDate")}
                            </Table.Head>
                            <Table.Head
                                onClick={() => handleSort("createdBy")}
                                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            >
                                Opprettet av{sortIndicator("createdBy")}
                            </Table.Head>
                            <Table.Head></Table.Head>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {commissions.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={7}>
                                    <Text>Ingen provisjoner registrert ennå</Text>
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            sortedCommissions.map((c) => (
                                <Table.Row key={c.id}>
                                    <Table.Cell>{c.salesType}</Table.Cell>
                                    <Table.Cell>{c.chain ? (CHAIN_LABELS[c.chain] ?? c.chain) : "Alle"}</Table.Cell>
                                    <Table.Cell>{c.amount}</Table.Cell>
                                    <Table.Cell>{formatDate(c.startDate)}</Table.Cell>
                                    <Table.Cell>{formatDate(c.endDate)}</Table.Cell>
                                    <Table.Cell>{c.createdBy}</Table.Cell>
                                    <Table.Cell>
                                        <Flex gap="200">
                                            <Button variant="secondary" onClick={() => handleEdit(c)}>
                                                Rediger
                                            </Button>
                                            <Button variant="secondary" onClick={() => handleDelete(c.id)}>
                                                Slett
                                            </Button>
                                        </Flex>
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        )}
                    </Table.Body>
                </Table>
            )}
        </div>
    );
};

export default CommissionAdmin;
